import { Button } from '@/components/form/Button';
import { Product, ReplacedProduct } from '@/services/api/contracts/catalog/types';
import { useProductReplacements, useProducts, useReplaceProduct } from '@/services/api/hooks/product';
import { IncomingProduct, Replacement } from '@/services/csv/helpers/parsers';
import { FC, useState } from 'react';

import SiArrowRight from '@/icons/SiArrowRight';
import { useResult } from '@/services/csv/useCSVService';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ProductCard } from '../../../../../components/catalogue/products/cards/ProductCard';
import { Result } from '../bulkUploadHelper';

interface ReplacementReviewGridProps {
  replacements: Replacement[];
}

const ReplacementReviewGrid: FC<ReplacementReviewGridProps> = ({ replacements }) => {
  return (
    <>
      {replacements &&
        replacements.length > 0 &&
        replacements.map(replacement =>
          replacement.Replaces.map(replacedProduct => {
            return (
              <div className="flex flex-col gap-2" key={replacedProduct.id}>
                <div className="flex flex-row gap-2">
                  <div className="flex flex-col">
                    <ProductCard
                      key={replacedProduct.id}
                      product={{
                        ...replacedProduct
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-center font-semibold flex-1">
                    <SiArrowRight className="text-3xl" />
                  </div>
                  <div className="flex flex-col">
                    <ProductCard
                      key={replacement.Product.id}
                      product={{
                        ...replacement.Product
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
    </>
  );
};

const findReplacements = (
  incomingProducts: IncomingProduct[],
  catalog: Product[],
  productReplacements: ReplacedProduct[]
) => {
  const isReplacement = (item: Replacement | undefined): item is Replacement =>
    !!item && item.Product !== undefined && item.Replaces.length > 0;
  const isProduct = (item: Product | undefined): item is Product => !!item;
  const replacements = incomingProducts
    .map((product: IncomingProduct) => {
      if (product.Replacements) {
        const replacer = catalog.find((ci: Product) => ci.productId === product.ManufacturerSKU);
        const existingReplacements = productReplacements.filter(
          (ci: ReplacedProduct) => ci.replacementProduct.id === replacer?.id
        );
        let replacements = product.Replacements.split(';')
          .map(sku => catalog.find((ci: Product) => ci.productId === sku))
          .filter(isProduct);
        if (existingReplacements) {
          const replacedProducts = existingReplacements.map(rp => rp.replacedProduct.id);
          replacements = replacements.filter(replacement => !replacedProducts.includes(replacement.id));
        }
        return { Product: replacer, Replaces: replacements } as Replacement;
      }
    })
    .filter(isReplacement);
  return replacements;
};

export const ReplacementStep = () => {
  const result = useResult<Result>();
  if (!result) throw Error('No result');

  const [catalog] = useProducts();
  const [productReplacements] = useProductReplacements();
  const replaceProducts = useReplaceProduct();

  const navigate = useNavigate();

  const [replacements] = useState<Replacement[]>(findReplacements(result.data, catalog, productReplacements));

  const finish = async () => {
    console.log(replacements);

    replacements.map(r => {
      r.Replaces.map(async rp => {
        await replaceProducts.mutateAsync({
          replacedProductId: rp.id,
          replacementProductId: r.Product.id,
          effectiveFrom: new Date()
        });
      });
    });

    toast.success('Product replacements applied');
    navigate('/catalogue');
  };

  return (
    <div className="flex flex-col gap-2">
      {replacements.length > 0 && <ReplacementReviewGrid replacements={replacements} />}
      <Button loading={replaceProducts.isLoading} onClick={finish}>
        {replacements.length == 0 ? 'No replacements, click to continue' : 'Finish'}
      </Button>
    </div>
  );
};
